<script setup>
/**
 * Catalog Loader
 * 3D animated loading screen — a GoFood parcel rotating in real CSS 3D,
 * with floating product cubes and a shimmer progress bar
 */
import { ref, onMounted, onUnmounted } from 'vue'

const loadingTexts = ['טוען מוצרים...', 'מכין את הקטלוג...', 'עוד רגע מוכן...']

const currentTextIndex = ref(0)
let textInterval = null

onMounted(() => {
  textInterval = setInterval(() => {
    currentTextIndex.value = (currentTextIndex.value + 1) % loadingTexts.length
  }, 2200)
})

onUnmounted(() => {
  if (textInterval) clearInterval(textInterval)
})
</script>

<template>
  <div class="catalog-loader">
    <div class="loader-content">
      <!-- 3D Stage -->
      <div class="stage">
        <div class="stage-glow"></div>

        <!-- Floating product mini-cubes (different depths) -->
        <div class="mini mini-amber">
          <div class="mini-cube">
            <div class="mf mf-front"></div>
            <div class="mf mf-back"></div>
            <div class="mf mf-right"></div>
            <div class="mf mf-left"></div>
            <div class="mf mf-top"></div>
            <div class="mf mf-bottom"></div>
          </div>
        </div>
        <div class="mini mini-light">
          <div class="mini-cube">
            <div class="mf mf-front"></div>
            <div class="mf mf-back"></div>
            <div class="mf mf-right"></div>
            <div class="mf mf-left"></div>
            <div class="mf mf-top"></div>
            <div class="mf mf-bottom"></div>
          </div>
        </div>
        <div class="mini mini-slate">
          <div class="mini-cube">
            <div class="mf mf-front"></div>
            <div class="mf mf-back"></div>
            <div class="mf mf-right"></div>
            <div class="mf mf-left"></div>
            <div class="mf mf-top"></div>
            <div class="mf mf-bottom"></div>
          </div>
        </div>

        <!-- Hero parcel -->
        <div class="parcel-float">
          <div class="parcel">
            <div class="face face-front">
              <span class="tape tape-h"></span>
              <span class="brand-label">GO FOOD</span>
            </div>
            <div class="face face-back">
              <span class="tape tape-h"></span>
              <span class="brand-label">GO FOOD</span>
            </div>
            <div class="face face-right"><span class="tape tape-h"></span></div>
            <div class="face face-left"><span class="tape tape-h"></span></div>
            <div class="face face-top">
              <span class="flap-seam"></span>
              <span class="tape tape-top"></span>
            </div>
            <div class="face face-bottom"></div>
          </div>
        </div>

        <!-- Breathing ground shadow -->
        <div class="parcel-shadow"></div>

        <!-- Ambient sparks -->
        <span class="spark s1"></span>
        <span class="spark s2"></span>
        <span class="spark s3"></span>
        <span class="spark s4"></span>
      </div>

      <!-- Loading text -->
      <div class="loader-text">
        <Transition name="msg" mode="out-in">
          <p class="loading-message" :key="currentTextIndex">
            {{ loadingTexts[currentTextIndex] }}
          </p>
        </Transition>
        <div class="progress-track">
          <div class="progress-bar"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.catalog-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  height: 100%;
  background: transparent;
  overflow: hidden;
  position: relative;
}

.loader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

/* ===== 3D Stage ===== */
.stage {
  position: relative;
  width: 320px;
  height: 240px;
  perspective: 900px;
}

.stage-glow {
  position: absolute;
  inset: 10% 8%;
  background:
    radial-gradient(closest-side, rgba(245, 158, 11, 0.1), transparent 70%),
    radial-gradient(closest-side, rgba(40, 53, 72, 0.06), transparent 75%);
  filter: blur(6px);
  border-radius: 50%;
}

/* --- Hero parcel --- */
.parcel-float {
  position: absolute;
  left: 50%;
  top: 42%;
  animation: parcelBob 3.4s ease-in-out infinite;
}

.parcel {
  --w: 116px; /* width  (front face) */
  --h: 88px; /* height */
  --d: 116px; /* depth  (side face)  */
  position: absolute;
  width: 0;
  height: 0;
  transform-style: preserve-3d;
  animation: parcelSpin 8s linear infinite;
}

@keyframes parcelSpin {
  from {
    transform: rotateX(-24deg) rotateY(0deg);
  }
  to {
    transform: rotateX(-24deg) rotateY(360deg);
  }
}

@keyframes parcelBob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-12px);
  }
}

.face {
  position: absolute;
  left: 50%;
  top: 50%;
  backface-visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.face-front,
.face-back {
  width: var(--w);
  height: var(--h);
  margin-left: calc(var(--w) / -2);
  margin-top: calc(var(--h) / -2);
  background: linear-gradient(165deg, #35465f 0%, #283548 55%, #223043 100%);
}

.face-front {
  transform: translateZ(calc(var(--d) / 2));
}

.face-back {
  transform: rotateY(180deg) translateZ(calc(var(--d) / 2));
}

.face-right,
.face-left {
  width: var(--d);
  height: var(--h);
  margin-left: calc(var(--d) / -2);
  margin-top: calc(var(--h) / -2);
  background: linear-gradient(165deg, #26344a 0%, #1e293b 60%, #192333 100%);
}

.face-right {
  transform: rotateY(90deg) translateZ(calc(var(--w) / 2));
}

.face-left {
  transform: rotateY(-90deg) translateZ(calc(var(--w) / 2));
}

.face-top,
.face-bottom {
  width: var(--w);
  height: var(--d);
  margin-left: calc(var(--w) / -2);
  margin-top: calc(var(--d) / -2);
}

.face-top {
  transform: rotateX(90deg) translateZ(calc(var(--h) / 2));
  background: linear-gradient(135deg, #48597a 0%, #3b4c68 60%, #33435c 100%);
}

.face-bottom {
  transform: rotateX(-90deg) translateZ(calc(var(--h) / 2));
  background: #0f172a;
}

/* Packing tape — horizontal band wrapping all four sides */
.tape-h {
  position: absolute;
  top: 26%;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(180deg, #fbbf24 0%, #f59e0b 65%, #e08a06 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

/* Tape closing the top flaps */
.tape-top {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 20px;
  margin-left: -10px;
  background: linear-gradient(90deg, #e08a06 0%, #f59e0b 35%, #fbbf24 55%, #f59e0b 100%);
}

.flap-seam {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  margin-left: -1px;
  background: rgba(15, 23, 42, 0.35);
}

.brand-label {
  position: relative;
  top: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #283548;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 3px 10px;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.25);
}

/* --- Ground shadow --- */
.parcel-shadow {
  position: absolute;
  left: 50%;
  top: 78%;
  width: 150px;
  height: 26px;
  margin-left: -75px;
  background: radial-gradient(closest-side, rgba(15, 23, 42, 0.28), transparent 72%);
  animation: shadowBreathe 3.4s ease-in-out infinite;
}

@keyframes shadowBreathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.82);
    opacity: 0.65;
  }
}

/* --- Floating product mini-cubes --- */
.mini {
  position: absolute;
  perspective: 500px;
}

.mini-cube {
  position: absolute;
  width: 0;
  height: 0;
  transform-style: preserve-3d;
  animation: miniSpin 6s linear infinite;
}

@keyframes miniSpin {
  from {
    transform: rotateX(-28deg) rotateY(0deg);
  }
  to {
    transform: rotateX(-28deg) rotateY(-360deg);
  }
}

.mf {
  position: absolute;
  left: 50%;
  top: 50%;
  width: var(--s);
  height: var(--s);
  margin-left: calc(var(--s) / -2);
  margin-top: calc(var(--s) / -2);
  backface-visibility: hidden;
  border-radius: 2px;
}

.mf-front {
  transform: translateZ(calc(var(--s) / 2));
}
.mf-back {
  transform: rotateY(180deg) translateZ(calc(var(--s) / 2));
}
.mf-right {
  transform: rotateY(90deg) translateZ(calc(var(--s) / 2));
}
.mf-left {
  transform: rotateY(-90deg) translateZ(calc(var(--s) / 2));
}
.mf-top {
  transform: rotateX(90deg) translateZ(calc(var(--s) / 2));
}
.mf-bottom {
  transform: rotateX(-90deg) translateZ(calc(var(--s) / 2));
}

/* Amber product cube */
.mini-amber {
  --s: 26px;
  left: 18%;
  top: 30%;
  animation: miniBob 2.8s ease-in-out infinite;
}

.mini-amber .mf-front,
.mini-amber .mf-back {
  background: #f59e0b;
}
.mini-amber .mf-right,
.mini-amber .mf-left {
  background: #d97706;
}
.mini-amber .mf-top {
  background: #fbbf24;
}
.mini-amber .mf-bottom {
  background: #b45309;
}

/* Light product cube */
.mini-light {
  --s: 20px;
  right: 16%;
  top: 22%;
  animation: miniBob 3.6s ease-in-out infinite 0.6s;
}

.mini-light .mini-cube {
  animation-duration: 7.5s;
}

.mini-light .mf-front,
.mini-light .mf-back {
  background: #f1f5f9;
}
.mini-light .mf-right,
.mini-light .mf-left {
  background: #cbd5e1;
}
.mini-light .mf-top {
  background: #ffffff;
}
.mini-light .mf-bottom {
  background: #94a3b8;
}

/* Slate product cube — farthest, slightly blurred for depth */
.mini-slate {
  --s: 15px;
  right: 11%;
  top: 70%;
  filter: blur(0.6px);
  opacity: 0.85;
  animation: miniBob 3.2s ease-in-out infinite 1.2s;
}

.mini-slate .mini-cube {
  animation-duration: 9s;
}

.mini-slate .mf-front,
.mini-slate .mf-back {
  background: #64748b;
}
.mini-slate .mf-right,
.mini-slate .mf-left {
  background: #475569;
}
.mini-slate .mf-top {
  background: #94a3b8;
}
.mini-slate .mf-bottom {
  background: #334155;
}

@keyframes miniBob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* --- Ambient sparks --- */
.spark {
  position: absolute;
  border-radius: 50%;
  background: #f59e0b;
  opacity: 0.5;
}

.s1 {
  width: 5px;
  height: 5px;
  top: 16%;
  left: 34%;
  animation: sparkFloat 3s ease-in-out infinite;
}

.s2 {
  width: 4px;
  height: 4px;
  top: 58%;
  left: 12%;
  background: #94a3b8;
  animation: sparkFloat 3.8s ease-in-out infinite 0.8s;
}

.s3 {
  width: 6px;
  height: 6px;
  top: 12%;
  right: 30%;
  animation: sparkFloat 3.4s ease-in-out infinite 1.4s;
}

.s4 {
  width: 4px;
  height: 4px;
  top: 44%;
  right: 8%;
  animation: sparkFloat 4.2s ease-in-out infinite 2s;
}

@keyframes sparkFloat {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-14px);
    opacity: 0.9;
  }
}

/* ===== Loading Text ===== */
.loader-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
}

.loading-message {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-secondary, #475569);
  margin: 0;
}

.msg-enter-active,
.msg-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.msg-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.msg-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Indeterminate shimmer bar */
.progress-track {
  position: relative;
  width: 180px;
  height: 5px;
  background: var(--border-color, #e2e8f0);
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40%;
  border-radius: 999px;
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
  animation: progressSlide 1.6s ease-in-out infinite alternate;
}

@keyframes progressSlide {
  from {
    left: 0%;
  }
  to {
    left: 60%;
  }
}

/* ===== Reduced motion ===== */
@media (prefers-reduced-motion: reduce) {
  .parcel,
  .parcel-float,
  .parcel-shadow,
  .mini,
  .mini-cube,
  .spark,
  .progress-bar {
    animation: none;
  }

  .parcel {
    transform: rotateX(-24deg) rotateY(35deg);
  }

  .mini-cube {
    transform: rotateX(-28deg) rotateY(40deg);
  }

  .progress-bar {
    left: 30%;
  }
}

/* ===== Responsive ===== */
@media (max-width: 767px) {
  .stage {
    width: 280px;
    height: 210px;
    transform: scale(0.9);
  }

  .loading-message {
    font-size: 1rem;
  }
}
</style>
